import { NewEvent } from "../declarations/interface";

class EventsCapture {
  /**
   * listener for every actions performed inside the application
   * @param {any} event
   */
  public getEventPayload = (event: any): NewEvent => {
    const eventType = event.type;
    const targetPath = this.getElementPath(event);
    const obj = { targetPath, eventType };
    const targetHash = this.hashCode(JSON.stringify(obj));
    const { top, left, height, width } = event.target.getBoundingClientRect();
    return {
      eventId: `${this.hashCode(`${event?.target?.innerText},${targetHash}`)}`,
      eventType: eventType,
      eventTimeStamp: new Date(),
      eventTargetHash: targetHash,
      eventTargetPath: "",
      top,
      left,
      height,
      width,
      eventName: this.getEventName(event),
      elementXPath: this.createXPathForElement(event.target),
    };
  };

  /**
   * hashing of target path
   * @param {string}targetPath
   * @return {number}
   */
  private hashCode = (targetPath: string): number => {
    console.log(targetPath);
    let hash = 0;
    if (targetPath.length == 0) return hash;
    for (let index = 0; index < targetPath.length; index++) {
      const char = targetPath.charCodeAt(index);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  };

  /**
   * element attributes are fetched for hashing
   * @param {Event}event
   * @return {Object}
   */
  private getElementPath = (
    event: Event
  ): {
    nodeName: string;
    nodeValue: string;
  }[][] => {
    let elementPaths: { nodeName: string; nodeValue: string }[][] = [];
    const composedPath = event.composedPath();
    for (let index = 0; index < composedPath.length; index++) {
      const element = composedPath[index] as any;
      elementPaths.push(this.getListofAttributes(element.attributes));
      if (element.tagName === "HTML") {
        elementPaths = [...elementPaths, [], []];
        break;
      }
    }
    return elementPaths;
  };

  /**
   * listofattributes are extracted from element for hashing
   * @param {NamedNodeMap}attributes
   * @return {Object}
   */
  private getListofAttributes(attributes: NamedNodeMap): {
    nodeName: string;
    nodeValue: string;
  }[] {
    const attributesList: { nodeName: string; nodeValue: string }[] = [];
    if (attributes) {
      Array.from(attributes).forEach((attribute) => {
        if (STANDARD_ATTRIBUTES_FOR_HASHING.includes(attribute.nodeName)) {
          attributesList.push({
            nodeName: attribute.nodeName,
            nodeValue: attribute.nodeValue || "",
          });
        }
      });
    }
    return attributesList;
  }

  /**
   * Method used to construct event name using the event type
   *
   * @param {any}event Event object
   * @return {string} Event name
   */
  private getEventName(event: any): string {
    let eventName = "";
    switch (event.type) {
      case "pointerdown":
        eventName = "Click Event (Pointer)";
        break;
      case "mouseup":
        eventName = "Click Event (Mouse)";
        break;
      case "keypress":
        eventName = `Key Event (${event.key})`;
        break;
      case "dblclick":
        eventName = "Double Click Event";
        break;
    }
    return eventName;
  }

  /**
   * Method used to construct xPath for a given element
   *
   * @param {any} elm
   * @return {string} elementXPath
   */
  private createXPathForElement(elm: any): string | null {
    const allNodes = document.getElementsByTagName("*");
    let segs;
    for (segs = []; elm && elm.nodeType == 1; elm = elm.parentNode) {
      if (elm.hasAttribute("id")) {
        let uniqueIdCount = 0;
        for (let n = 0; n < allNodes.length; n++) {
          if (allNodes[n].hasAttribute("id") && allNodes[n].id == elm.id) {
            uniqueIdCount++;
          }
          if (uniqueIdCount > 1) break;
        }
        if (uniqueIdCount == 1) {
          segs.unshift('id("' + elm.getAttribute("id") + '")');
          return segs.join("/");
        } else {
          segs.unshift(
            elm.localName.toLowerCase() +
              '[@id="' +
              elm.getAttribute("id") +
              '"]'
          );
        }
      } else if (elm.hasAttribute("class")) {
        segs.unshift(
          elm.localName.toLowerCase() +
            '[@class="' +
            elm.getAttribute("class") +
            '"]'
        );
      } else {
        let i;
        let sib;
        for (i = 1, sib = elm.previousSibling; sib; sib = sib.previousSibling) {
          if (sib.localName == elm.localName) i++;
        }
        segs.unshift(elm.localName.toLowerCase() + "[" + i + "]");
      }
    }
    return segs.length ? "/" + segs.join("/") : null;
  }
}

export default new EventsCapture();

const STANDARD_ATTRIBUTES_FOR_HASHING = [
  "accept",
  "accesskey",
  "action",
  "align",
  "allow",
  "alt",
  "async",
  "autocapitalize",
  "autocomplete",
  "autofocus",
  "autoplay",
  "background",
  "bgcolor",
  "border",
  "buffered",
  "capture",
  "challenge",
  "charset",
  "checked",
  "cite",
  "class",
  "code",
  "codebase",
  "color",
  "cols",
  "colspan",
  "content",
  "contenteditable",
  "contextmenu",
  "controls",
  "coords",
  "crossorigin",
  "csp",
  "data",
  "datetime",
  "decoding",
  "default",
  "defer",
  "dir",
  "dirname",
  "disabled",
  "download",
  "draggable",
  "enctype",
  "enterkeyhint",
  "for",
  "form",
  "formaction",
  "formenctype",
  "formmethod",
  "formnovalidate",
  "formtarget",
  "headers",
  "height",
  "hidden",
  "high",
  // "href", should not include this attribute for hashing
  "hreflang",
  "http",
  "icon",
  "id",
  "importance",
  "integrity",
  "intrinsicsize",
  "inputmode",
  "ismap",
  "itemprop",
  "keytype",
  "kind",
  "label",
  "lang",
  "language",
  "loading",
  "list",
  "loop",
  "low",
  "max",
  "maxlength",
  "minlength",
  "media",
  "method",
  "min",
  "multiple",
  "muted",
  "name",
  "novalidate",
  "open",
  "optimum",
  "pattern",
  "ping",
  "placeholder",
  "poster",
  "preload",
  "radiogroup",
  "readonly",
  "referrerpolicy",
  "rel",
  "required",
  "reversed",
  "role",
  "rows",
  "rowspan",
  "sandbox",
  "scope",
  "scoped",
  "selected",
  "shape",
  "size",
  "sizes",
  "slot",
  "span",
  "spellcheck",
  // "src", should not include this attribute for hashing
  "srcdoc",
  "srclang",
  "srcset",
  "start",
  "step",
  "style",
  "summary",
  "tabindex",
  "target",
  "title",
  "translate",
  "type",
  "usemap",
  "value",
  "width",
  "wrap",
];
